import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, InputNumber, Modal, Typography, notification } from "antd";
import {
  createRazorpayOrder,
  initiateRazorpayCheckout,
  loadRazorpay,
  verifyRazorpayPayment,
} from "../../util/RazorpayHelper";
import {
  CURRENCY_CODE_INR,
  DEFAULT_ENQUIRY_CREDIT_CONFIG,
} from "../../util/Constant";
import { useSelector } from "react-redux";

const PurchasePointModal = ({
  isPurchasePointModalOpen,
  onClose,
  openSuccessModal,
  openFailedModal,
  openLoadingModal,
  closeLoadingModal,
  refreshBalance,
  walletSummary,
}) => {
  const authUser = useSelector(({ auth }) => auth.userData);
  const minAddFundsAmount =
    walletSummary?.minAddFundsAmount ||
    DEFAULT_ENQUIRY_CREDIT_CONFIG.minAddFundsAmount;
  const maxAddFundsAmount =
    walletSummary?.maxAddFundsAmount ||
    DEFAULT_ENQUIRY_CREDIT_CONFIG.maxAddFundsAmount;
  const [amount, setAmount] = useState(minAddFundsAmount);
  const { Text } = Typography;

  useEffect(() => {
    loadRazorpay();
  }, []);

  useEffect(() => {
    setAmount(minAddFundsAmount);
  }, [minAddFundsAmount]);

  const handleClosePurchasePointModal = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleAmountChange = (value) => {
    const numericValue = Number(value || minAddFundsAmount);
    const clampedValue = Math.min(
      Math.max(numericValue, minAddFundsAmount),
      maxAddFundsAmount
    );
    setAmount(clampedValue);
  };

  const fundsPurchaseSuccessHandler = useCallback(
    async (response) => {
      try {
        openLoadingModal();
        const paymentVerification = await verifyRazorpayPayment(
          response.razorpay_order_id,
          response.razorpay_payment_id,
          authUser?.uuid,
          response.razorpay_signature,
          amount
        );
      if (!paymentVerification?.isValid) {
        throw new Error("Payment verification failed");
      }

      await refreshBalance?.();
      closeLoadingModal();
      openSuccessModal(amount);
      } catch (error) {
        openFailedModal();
        closeLoadingModal();
      }
    },
    [
      amount,
      authUser.uuid,
      openLoadingModal,
      refreshBalance,
      closeLoadingModal,
      openSuccessModal,
      openFailedModal,
    ]
  );

  const fundsPurchaseErrorHandler = useCallback(
    async (response) => {
      let errorReason;
      let paymentId;
      let orderId;

      try {
        openLoadingModal();
        errorReason = response?.error?.reason;
        paymentId = response?.error?.metadata?.payment_id;
        orderId = response?.error?.metadata?.order_id;
      } catch (error) {
        console.error("Error extracting payment failure details:", error);
      }

      try {
        await verifyRazorpayPayment(
          orderId,
          paymentId,
          authUser?.uuid,
          undefined,
          undefined,
          errorReason
        );
        closeLoadingModal();
      } catch (backendError) {
        console.error(
          "Failed to report payment failure to backend:",
          backendError
        );
      }

      openFailedModal();
    },
    [authUser.uuid, closeLoadingModal, openFailedModal, openLoadingModal]
  );

  const initiateRazorpayPaymentFlow = useCallback(async () => {
    try {
      handleClosePurchasePointModal();
      openLoadingModal();
      const amountInSmallestUnit = amount * 100;
      const order = await createRazorpayOrder(
        amountInSmallestUnit,
        CURRENCY_CODE_INR
      );
      closeLoadingModal();
      initiateRazorpayCheckout(
        order,
        {
          contact: authUser.mobile,
        },
        fundsPurchaseSuccessHandler,
        fundsPurchaseErrorHandler
      );
    } catch (error) {
      console.error(
        "Create wallet Razorpay order failed:",
        error?.response?.data || error?.message || error
      );
      notification.error({
        message: "Unable to start payment",
        description:
          error?.response?.data?.description ||
          error?.response?.data?.message ||
          "Failed to create Razorpay order for adding funds.",
      });
      openFailedModal();
      closeLoadingModal();
    }
  }, [
    amount,
    authUser.mobile,
    authUser.uuid,
    closeLoadingModal,
    fundsPurchaseErrorHandler,
    fundsPurchaseSuccessHandler,
    openFailedModal,
    openLoadingModal,
  ]);

  return (
    <div>
      <Modal
        open={isPurchasePointModalOpen}
        onCancel={handleClosePurchasePointModal}
        footer={null}
        closable={false}
        width="375px"
        maskClosable={false}
        className="purchase-modal"
      >
        <div className="modal-header">
          <img
            onClick={handleClosePurchasePointModal}
            src="/assets/images/back.png"
            alt="back"
            className="back-icon-custom"
          />
          <Text className="modal-title">Add Funds</Text>
        </div>

        <div className="modal-content">
          <div>
            <Text className="description">
              Enter the amount you want to add to your balance
            </Text>
          </div>
          <div className="counter-container">
            <InputNumber
              min={minAddFundsAmount}
              max={maxAddFundsAmount}
              value={amount}
              onChange={handleAmountChange}
              style={{ width: "100%" }}
              formatter={(value) =>
                `INR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/INR\s?|(,*)/g, "")}
            />
          </div>

          <div>
            <Text className="price-details">
              Amount payable = INR {Number(amount || 0).toLocaleString("en-IN")}
            </Text>
          </div>
        </div>

        <Card className="note-card">
          <div className="note-header">
            <img
              src="/assets/images/infoIcon.png"
              alt="icon"
              className="info-icon"
            />
            <Text className="note-title">Note</Text>
          </div>
          <Text className="note-content">
            Minimum add funds amount is INR {minAddFundsAmount}. Maximum is INR{" "}
            {Number(maxAddFundsAmount).toLocaleString("en-IN")}.
          </Text>
        </Card>

        <div className="button-container">
          <Button
            onClick={initiateRazorpayPaymentFlow}
            className="primary-button"
            size="small"
            style={{
              width: "100%",
              fontSize: "16px",
              textAlign: "center",
              marginBottom: "20px",
              height: "50px",
              lineHeight: "20px",
            }}
          >
            Proceed to Payment
          </Button>
          <Button
            onClick={handleClosePurchasePointModal}
            size="small"
            className="secondary-button"
            style={{
              width: "100%",
              fontSize: "16px",
              textAlign: "center",
              marginBottom: "20px",
              height: "45px",
              lineHeight: "20px",
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PurchasePointModal;
